import { Collapse, Typography } from "antd"
import React from "react"

import { EducationArticle, type EducationArticleLabels } from "../../../components/Education/EducationArticle.tsx"
import { cn } from "../../../utils/cn.ts"
import type { CitationView } from "../../Lead/LeadWorkspacePage/education/types/education.types.ts"

const { Title, Text } = Typography

const isLinkableCitationUrl = (url: string | null): url is string =>
  !!url && /^https?:\/\//i.test(url)

export interface ProjectEducationGuideBodyLabels {
  /** "Project guide for {projectLabel}" — a function, not a template string,
   *  because the label itself is only known once this component is called. */
  guideTitle: (projectLabel: string) => string
  preparedFor: string
  sharedBy: string
  updated: string
  sourcesHeading: string
  disclaimer: string
  articleLabels: EducationArticleLabels
}

export interface ProjectEducationGuideBodyProps {
  projectLabel: string
  preparedFor?: string | null
  sharedBy?: string | null
  /** Already formatted in the caller's own date convention (account format
   *  internally, or the public page's own fixed format) — this component
   *  never formats a date itself. */
  updatedLabel?: string | null
  html: string
  citations: CitationView[]
  /** The edition's "important notes" — rendered inside the same collapsible
   *  section as sources rather than a second heading. */
  limitations?: string[]
  /** A banner slot above the title — Task 6's internal preview uses it for
   *  "Preview: this is what your customer will see"; the real public page
   *  (Task 9) leaves it empty. */
  banner?: React.ReactNode
  /** Task 9's edition selector (Overview / Section 2 / Section 3) — omitted by
   *  Task 6's preview, which always shows exactly one edition. */
  editionSelector?: React.ReactNode
  /** Task 9's live audio player — deliberately omitted by Task 6's internal
   *  preview modal, which renders "no live actions". */
  audioSlot?: React.ReactNode
  /** Task 9's "Want more detail?" panel and Contact/Report issue footer —
   *  also omitted by Task 6's preview for the same reason. */
  footerSlot?: React.ReactNode
  labels: ProjectEducationGuideBodyLabels
  className?: string
}

/**
 * The customer guide's own body — title, "Prepared for / Shared by /
 * Updated" meta line, the rendered edition, its sources and notes, and the
 * educational-content disclaimer. Shared verbatim by Task 6's internal
 * "Preview as customer" modal and Task 9's real public customer page: it
 * carries no audience-specific chrome of its own (no app navigation, no
 * network calls), only the content and the slots each caller fills in.
 *
 * Takes its copy as an explicit `labels` prop rather than calling
 * `useTranslation()` — the same convention `EducationArticle` and
 * `EducationAudioPlayer` already use — so the public page (Task 9) can mount
 * this outside the authenticated app's data flow without this component
 * needing to know the difference.
 */
export function ProjectEducationGuideBody({
  projectLabel,
  preparedFor,
  sharedBy,
  updatedLabel,
  html,
  citations,
  limitations = [],
  banner,
  editionSelector,
  audioSlot,
  footerSlot,
  labels,
  className
}: ProjectEducationGuideBodyProps) {
  const metaParts = [
    preparedFor ? `${labels.preparedFor} ${preparedFor}` : null,
    sharedBy ? `${labels.sharedBy} ${sharedBy}` : null,
    updatedLabel ? `${labels.updated} ${updatedLabel}` : null
  ].filter((part): part is string => !!part)

  const hasSourcesSection = citations.length > 0 || limitations.length > 0

  return (
    <div
      className={cn("project-education-guide-body", className)}
      data-testid={"project-education-guide-body"}
    >
      {banner}

      <Title level={4} className={"!mb-1"}>
        {labels.guideTitle(projectLabel)}
      </Title>

      {metaParts.length > 0 && (
        <Text type={"secondary"} className={"block mb-3"}>
          {metaParts.join(" | ")}
        </Text>
      )}

      {editionSelector}
      {audioSlot}

      <EducationArticle
        html={html}
        citations={[]}
        className={"mt-3"}
        labels={labels.articleLabels}
      />

      {hasSourcesSection && (
        <Collapse
          className={"mt-4"}
          items={[
            {
              key: "sources",
              label: labels.sourcesHeading,
              children: (
                <div data-testid={"guide-sources-and-notes"}>
                  {citations.length > 0 && (
                    <ol className={"list-decimal space-y-1 pl-5 text-sm"}>
                      {citations.map((citation) => (
                        <li key={citation.id}>
                          {isLinkableCitationUrl(citation.url) ? (
                            <a href={citation.url} target={"_blank"} rel={"noopener noreferrer"}>
                              {citation.label}
                            </a>
                          ) : (
                            citation.label
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                  {limitations.length > 0 && (
                    <ul className={"list-disc space-y-1 pl-5 text-sm mt-2"}>
                      {limitations.map((note, index) => (
                        <li key={index}>{note}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )
            }
          ]}
        />
      )}

      <Text type={"secondary"} className={"block mt-4 text-xs"}>
        {labels.disclaimer}
      </Text>

      {footerSlot}
    </div>
  )
}
